import { isAfter, isBefore } from 'date-fns';
import _get from 'lodash/get';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskTimelineFrameEnum,
  TaskTimelineGroupByEnum,
  useTasksTimelineQuery,
} from 'graphql-common';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import {
  getPeriodEndDate,
  getPeriodStartDate,
} from 'routes/Dashboard/TasksTimeline/utils/getPeriodDate';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { TASKS_TIMELINE_FILTER_EMPTY_VALUES } from 'routes/Dashboard/TasksTimeline/utils/useFilterFields';
import { SelectOption } from '@lib/components/Select/types';
import { captureException } from 'utils/captureException';
import { ListQueryHookProps } from '../types';

export default function useDashboardTasksCompletionsQueryHook({
  filterParams = {},
  loading: defaultLoading,
  skip,
  tasks,
}: ListQueryHookProps & { tasks: Task[] }) {
  const timelineView = (filterParams[TasksFilterFieldNames.TimelineView] ||
    TASKS_TIMELINE_FILTER_EMPTY_VALUES[
      TasksFilterFieldNames.TimelineView
    ]) as TaskTimelineFrameEnum;
  const validityHintsParam = filterParams[
    TasksFilterFieldNames.ValidityHints
  ] as SelectOption[];
  const validityHints = validityHintsParam?.map(
    ({ value }) => value as FormTemplateQuestionFieldOptionsValidityHintEnum,
  );
  let endDateParam = filterParams[
    TasksFilterFieldNames.TimelineEndDate
  ] as string;
  if (!endDateParam) {
    const nowDate = new Date();
    endDateParam = getFormattedDate(
      getPeriodEndDate(nowDate.toISOString(), timelineView),
      dateFormatForApi,
    ) as string;
  }
  const period = _get(filterParams, TasksFilterFieldNames.Period) as string[];
  const periodStartParam = period?.[0];
  const periodEndParam = period?.[1];

  const ids = tasks.map(({ id }) => id);

  const periodStart = getFormattedDate(
    periodStartParam,
    dateFormatForApi,
  ) as string;
  const periodEnd = getFormattedDate(
    periodEndParam,
    dateFormatForApi,
  ) as string;
  const timelineStart = getFormattedDate(
    getPeriodStartDate(endDateParam, timelineView),
    dateFormatForApi,
  ) as string;
  const timelineEnd = getFormattedDate(
    getPeriodEndDate(endDateParam, timelineView),
    dateFormatForApi,
  ) as string;
  const computedStartDate =
    periodStart && isAfter(new Date(periodStart), new Date(timelineStart))
      ? periodStart
      : timelineStart;
  const computedEndDate =
    periodEnd && isBefore(new Date(periodEnd), new Date(timelineEnd))
      ? periodEnd
      : timelineEnd;
  const dateRange = {
    startDate: computedStartDate,
    // Clamp endDate so it's never before startDate (can happen when period filter
    // and timeline view window don't overlap, e.g. period ends in Q4 2025 but
    // timeline shows Q1 2026).
    endDate: isBefore(new Date(computedEndDate), new Date(computedStartDate))
      ? computedStartDate
      : computedEndDate,
  };
  let groupBy = TaskTimelineGroupByEnum.Day;
  if (
    TaskTimelineFrameEnum.Year === timelineView ||
    TaskTimelineFrameEnum.Quarter === timelineView
  ) {
    groupBy = TaskTimelineGroupByEnum.Month;
  }
  const { data, loading } = useTasksTimelineQuery({
    skip: skip || !tasks.length || !endDateParam || defaultLoading,
    fetchPolicy: 'no-cache',
    variables: {
      taskIds: ids,
      queryData: {
        groupBy,
        dateRange,
        validityHints,
      },
    },
    onError: (e) => captureException(e),
  });

  return { loading, data: data?.tasksTimeline, dataGroupBy: timelineView };
}
